import React, { useState, useEffect } from 'react'
import { Shield, Loader, Fingerprint } from 'lucide-react'
import { startAuthentication } from '@simplewebauthn/browser'
import { setAuthToken } from '../utils/auth'

interface AdminLoginProps {
  onLogin: (email: string) => void
}

interface AuthConfig {
  googleEnabled: boolean
  oktaEnabled: boolean
  oktaDomain?: string
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLogin }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [authConfig, setAuthConfig] = useState<AuthConfig>({ googleEnabled: true, oktaEnabled: false })
  const [showEmailInput, setShowEmailInput] = useState(false)
  const [email, setEmail] = useState('')
  const [passkeySupported, setPasskeySupported] = useState(false)

  useEffect(() => {
    // Check if WebAuthn is supported
    if (window.PublicKeyCredential) {
      setPasskeySupported(true)
    }

    // Fetch authentication configuration
    fetch('/api/auth/config')
      .then(res => res.json())
      .then(config => setAuthConfig(config))
      .catch(err => {
        console.error('Failed to load auth config:', err)
        // Default to Google only
        setAuthConfig({ googleEnabled: true, oktaEnabled: false })
      })
  }, [])

  const handleGoogleSSO = () => {
    setIsLoading(true)
    setError('')

    // Redirect to Google OAuth for admin authentication
    window.location.href = '/auth/google'
  }

  const handleOktaSSO = () => {
    setIsLoading(true)
    setError('')

    // Redirect to Okta OAuth for admin authentication
    window.location.href = '/auth/okta'
  }

  const handlePasskeyLogin = async () => {
    if (!email) {
      setError('Please enter your email address')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      // Get tenant ID (admins access specific tenants)
      const tenantId = sessionStorage.getItem('tenant-id')

      if (!tenantId) {
        setError('Tenant configuration not found. Please refresh the page.')
        setIsLoading(false)
        return
      }

      // Get authentication options from server
      const optionsResponse = await fetch('/api/auth/passkey/login/options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, tenantId })
      })

      if (!optionsResponse.ok) {
        const data = await optionsResponse.json()
        throw new Error(data.error || 'Failed to get authentication options')
      }

      const options = await optionsResponse.json()

      // Trigger device authentication (TouchID, FaceID, etc.)
      const credential = await startAuthentication(options)

      // Verify authentication with server
      const verifyResponse = await fetch('/api/auth/passkey/login/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, credential, tenantId })
      })

      if (!verifyResponse.ok) {
        const data = await verifyResponse.json()
        throw new Error(data.error || 'Authentication failed')
      }

      const data = await verifyResponse.json()

      if (data.success) {
        setAuthToken(data.token)
        onLogin(email)
      } else {
        throw new Error('Authentication failed')
      }

    } catch (err: any) {
      console.error('Passkey login error:', err)
      setError(err.message || 'Failed to authenticate with passkey')
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-lg">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            Trust Center Admin
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to access the admin dashboard
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700 mb-3">
              <strong>Admin Access Requirements:</strong>
            </p>
            <ul className="text-sm text-blue-600 space-y-1">
              <li>• Must use authorized organization email</li>
              <li>• Must be registered as an admin user</li>
              <li>• SSO authentication required</li>
            </ul>
          </div>

          <div className="space-y-3">
            {/* Passkey Login */}
            {passkeySupported && (
              <>
                {showEmailInput ? (
                  <div className="space-y-3">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handlePasskeyLogin()}
                      placeholder="Enter your email"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={handlePasskeyLogin}
                      disabled={isLoading || !email}
                      className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                    >
                      {isLoading ? (
                        <>
                          <Loader className="h-4 w-4 animate-spin mr-2" />
                          Authenticating...
                        </>
                      ) : (
                        <>
                          <Fingerprint className="h-5 w-5 mr-2" />
                          Sign in with Passkey
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowEmailInput(false)}
                      className="text-sm text-gray-600 hover:text-gray-800 w-full text-center"
                    >
                      ← Back to other options
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowEmailInput(true)}
                    disabled={isLoading}
                    className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                  >
                    <Fingerprint className="h-5 w-5 mr-2" />
                    Sign in with Passkey (TouchID/FaceID)
                  </button>
                )}

                {!showEmailInput && (
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-gray-50 text-gray-500">or</span>
                    </div>
                  </div>
                )}
              </>
            )}

            {!showEmailInput && authConfig.googleEnabled && (
              <button
                type="button"
                onClick={handleGoogleSSO}
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                {isLoading ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin mr-2" />
                    Redirecting to Google...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Sign in with Google
                  </>
                )}
              </button>
            )}

            {!showEmailInput && authConfig.oktaEnabled && (
              <button
                type="button"
                onClick={handleOktaSSO}
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                {isLoading ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin mr-2" />
                    Redirecting to Okta...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.25 11.25v1.5h1.5v-1.5h-1.5zm0-9c-4.96 0-9 4.04-9 9s4.04 9 9 9 9-4.04 9-9-4.04-9-9-9zm0 16.5c-4.14 0-7.5-3.36-7.5-7.5s3.36-7.5 7.5-7.5 7.5 3.36 7.5 7.5-3.36 7.5-7.5 7.5z"/>
                    </svg>
                    Sign in with Okta
                  </>
                )}
              </button>
            )}
          </div>

          <div className="text-center">
            <a href="/" className="text-sm text-primary-600 hover:text-primary-700">
              ← Back to Trust Center
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminLogin